import type { RefObject } from 'react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import _ from 'lodash'
import {
  VStack,
  FormControl as ChakraFormControl,
  Input as ChakraInput,
  FormErrorMessage as ChakraFormErrorMessage,
} from '@chakra-ui/react'

import type { InputFormProps as Props } from '../../../interfaces'
import { getFieldsMapping } from '../../../../utils/mediaLibrary'
import OperatorMenu from '../OperatorMenu'

const LocationInputForm = (props: Props): React.ReactElement => {
  const {
    searchQueryJson,
    validationObject,
    groupId,
    filterId,
    initialFocusRef,
    setSearchQueryJson,
  } = props

  const [dirty, setDirty] = useState<boolean>(false)

  const expression = useMemo(
    () => searchQueryJson.filterGroups[groupId]?.expressions[filterId],
    [searchQueryJson, groupId, filterId],
  )

  const error: string | null = useMemo(
    () => validationObject.errors[groupId][filterId],
    [validationObject, groupId, filterId],
  )

  const handleOperatorChange = useCallback(
    (newOperator: string) => {
      const newSearchQueryJson = _.cloneDeep(searchQueryJson)
      newSearchQueryJson.filterGroups[groupId].expressions[filterId].operator =
        newOperator
      newSearchQueryJson.filterGroups[groupId].expressions[filterId].value =
        getFieldsMapping({ fieldName: expression.fieldName }).getInitialValue(
          newOperator,
        )
      setSearchQueryJson(newSearchQueryJson)
    },
    [searchQueryJson, groupId, filterId],
  )

  const handleValueChange = useCallback(
    (newValue: string) => {
      const newSearchQueryJson = _.cloneDeep(searchQueryJson)
      newSearchQueryJson.filterGroups[groupId].expressions[filterId].value =
        newValue
      setSearchQueryJson(newSearchQueryJson)
    },
    [searchQueryJson, groupId, filterId],
  )

  useEffect(() => {
    initialFocusRef.current?.focus()
  }, [initialFocusRef, expression.operator])

  return (
    <ChakraFormControl width="full" isInvalid={error !== null && dirty}>
      <VStack width="full" spacing="0.5em" alignItems="stretch">
        <OperatorMenu
          width="full"
          expression={expression}
          handleOperatorChange={handleOperatorChange}
        />

        {['GEO_POINT'].includes(expression.operator) && (
          <ChakraInput
            ref={initialFocusRef as RefObject<HTMLInputElement>}
            width="full"
            placeholder="Eg. 40,100 5km"
            name={expression.fieldName}
            value={expression.value as string}
            onChange={e => {
              handleValueChange(e.target.value)
              setDirty(true)
            }}
            bgColor="white"
          />
        )}
        {['GEO_BOX', 'GEO_POLYGON'].includes(expression.operator) && (
          <ChakraInput
            ref={initialFocusRef as RefObject<HTMLInputElement>}
            width="full"
            placeholder={`Eg. 40,100|50,101${
              expression.operator === 'GEO_POLYGON' ? '|60,90|70,120' : ''
            }`}
            name={expression.fieldName}
            value={expression.value as string}
            onChange={e => {
              handleValueChange(e.target.value)
              setDirty(true)
            }}
            bgColor="white"
          />
        )}

        <ChakraFormErrorMessage>{error}</ChakraFormErrorMessage>
      </VStack>
    </ChakraFormControl>
  )
}

export default LocationInputForm
